import React, { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useToastStore } from '@/store';
import Icon from '@/components/Icon';
import useRequireAuth from '@/hooks/useRequireAuth';
import { trackEvent } from '@/constants/utils/analytics';
import { mediumTap } from '@/constants/utils/haptics';

/**
 * Heart control to bookmark a tool. Anonymous visitors get the standard
 * `AuthCTAModal` (via `useRequireAuth`) instead of a hard sign-in redirect.
 * Stops press propagation so parent cards / rows do not navigate.
 */
function ToolFavoriteHeart({ tool, hitSlop = 10 }) {
  const { colors } = useTheme();
  const { addToast } = useToastStore(s => s.actions);
  const hasUser = useAuthStore(s => Boolean(s.user?.id));
  const favoriteToolIds = useAuthStore(s => s.user?.favoriteToolIds);
  const toggleFavoriteTool = useAuthStore(s => s.toggleFavoriteTool);
  const requireAuth = useRequireAuth();

  const toolId = tool?.id != null ? String(tool.id) : '';
  const isFavorited = !!(toolId && (favoriteToolIds || []).map(String).includes(toolId));

  const styles = useMemo(() => StyleSheet.create({
    hit: {
      padding: 6,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
  }), []);

  const onPress = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (!toolId) return;
    const ran = requireAuth(
      async () => {
        try {
          const nowFavorited = await toggleFavoriteTool(toolId);
          mediumTap();
          trackEvent('tools', nowFavorited ? 'tools_favorite' : 'tools_unfavorite', tool?.slug || toolId);
        } catch (_) {
          addToast('Could not update favorite', 'error');
        }
      },
      {
        source: 'tools_favorite_heart',
        title: 'Sign in to favorite tools',
        subtitle: 'Favorites are tied to your account so they follow you across devices.',
        icon: 'heart',
      }
    );
    if (ran === undefined) {
      trackEvent('tools', 'tools_favorite_auth_required', tool?.slug || '');
    }
  }, [toolId, tool?.slug, requireAuth, toggleFavoriteTool, addToast]);

  // Favoriting is account-bound; logged-out visitors don't see the control.
  if (!toolId || !hasUser) return null;

  return (
    <Pressable
      onPress={onPress}
      hitSlop={hitSlop}
      style={({ hovered, pressed }) => [
        styles.hit,
        (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
      ]}
      accessibilityRole="button"
      accessibilityLabel={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      accessibilityState={{ selected: isFavorited }}
    >
      <Icon
        name="heart"
        size={15}
        solid={isFavorited}
        color={isFavorited ? '#ef4444' : colors.text_color_tertiary}
      />
    </Pressable>
  );
}

export default React.memo(ToolFavoriteHeart);
